import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {useMeStore} from '@progyny/store/meStore'

export enum ClaimSteps {
  ADDRESS = 'ADDRESS',
  TREATMENT = 'TREATMENT',
  DOCUMENTS = 'DOCUMENTS',
  MEDICAL_INFORMATION = 'MEDICAL_INFORMATION',
  PAYMENT_DETAILS = 'PAYMENT_DETAILS',
  OVERVIEW = 'OVERVIEW',
  CLAIM_SUBMISSION = 'CLAIM_SUBMISSION',
}

export const useClaimStepMeta = (claimRequested?: string) => {
  const {t} = useTranslation()
  const user = useMeStore((s) => s.user)
  const reimbursementMode = user?.member?.contract?.reimbursementMode
  const isMedicallyNecessaryRelevant =
    user?.member?.isMedicallyNecessaryRelevant ?? false

  return useMemo(() => {
    if (!claimRequested || !reimbursementMode) {
      return {
        stepEnumList: [],
        stepLabels: [],
        stepMap: {},
        isMedicallyNecessaryRelevant,
      }
    }

    const isMyself = claimRequested === 'myself'

    const rawSteps: ClaimSteps[] =
      reimbursementMode === 'payroll'
        ? isMyself
          ? [
              ClaimSteps.ADDRESS,
              ClaimSteps.TREATMENT,
              ClaimSteps.MEDICAL_INFORMATION,
              ClaimSteps.DOCUMENTS,
              ClaimSteps.OVERVIEW,
            ]
          : [
              ClaimSteps.TREATMENT,
              ClaimSteps.DOCUMENTS,
              ClaimSteps.MEDICAL_INFORMATION,
              ClaimSteps.OVERVIEW,
            ]
        : reimbursementMode === 'bank_transfer'
          ? isMyself
            ? [
                ClaimSteps.ADDRESS,
                ClaimSteps.TREATMENT,
                ClaimSteps.DOCUMENTS,
                ClaimSteps.MEDICAL_INFORMATION,
                ClaimSteps.PAYMENT_DETAILS,
                ClaimSteps.OVERVIEW,
              ]
            : [
                ClaimSteps.TREATMENT,
                ClaimSteps.DOCUMENTS,
                ClaimSteps.MEDICAL_INFORMATION,
                ClaimSteps.PAYMENT_DETAILS,
                ClaimSteps.OVERVIEW,
              ]
          : []

    const steps: ClaimSteps[] = isMedicallyNecessaryRelevant
      ? rawSteps
      : rawSteps.filter((step) => step !== ClaimSteps.MEDICAL_INFORMATION)

    const stepLabels = steps.map((step) => {
      switch (step) {
        case ClaimSteps.ADDRESS:
          return t('profile_personal_info_address_section')
        case ClaimSteps.TREATMENT:
          return t('budget_history_request_overview_treatment_service')
        case ClaimSteps.DOCUMENTS:
          return t('budget_history_request_overview_documents')
        case ClaimSteps.MEDICAL_INFORMATION:
          return t('budget_history_request_overview_medical_information')
        case ClaimSteps.PAYMENT_DETAILS:
          return t('budget_hook_claim_step_meta_payment_details')
        case ClaimSteps.OVERVIEW:
          return t('budget_hook_claim_step_meta_overview')
        case ClaimSteps.CLAIM_SUBMISSION:
          return t('budget_hook_claim_step_meta_claim_submission')
        default:
          return ''
      }
    })

    const stepMap = steps.reduce<Record<string, ClaimSteps>>((acc, step) => {
      const path = getPathFromStep(step)
      acc[path] = step
      return acc
    }, {})

    return {
      stepEnumList: steps,
      stepLabels,
      stepMap,
      isMedicallyNecessaryRelevant,
    }
  }, [reimbursementMode, claimRequested, isMedicallyNecessaryRelevant, t])
}

const getPathFromStep = (step: ClaimSteps): string => {
  switch (step) {
    case ClaimSteps.ADDRESS:
      return '/address'
    case ClaimSteps.TREATMENT:
      return '/treatment'
    case ClaimSteps.DOCUMENTS:
      return '/documents'
    case ClaimSteps.MEDICAL_INFORMATION:
      return '/medical-information'
    case ClaimSteps.PAYMENT_DETAILS:
      return '/payments'
    case ClaimSteps.OVERVIEW:
      return '/overview'
    default:
      return ''
  }
}
